/**
 * 상회칠판 고민 칸 (시안 09 · 08 6.4 B5 · B6, 08 결정 36, D52).
 *
 *   「고민 올리기」(52, --brand, + 20) → 내 고민 카드(최신 위): 상태 꼬리표(답하는 중 · 답을 받았어요)
 *   · 예전 단골 글이면 「내가 쓴 글」 · 오른쪽 시각 → 본문 16/26 → 답한 멤버 초상 22 겹침 + 이름. 탭 → 고민 글
 *
 * - 비었을 때: 이모(40) 「그냥 수다는 별채에서 해. 여긴 같이 고민할 것만 적어 둬.」
 * - 위기 신호가 애매해 이모가 확인 한마디를 단 글(D17)은 확인 카드, 위기 글은 안전 카드를 함께 둡니다
 */
import { Plus } from 'lucide-react';
import { getMember } from '@config/members/index.js';
import { Avatar } from '../../components/Avatar.jsx';
import { SafetyCard } from '../../components/SafetyCard.jsx';
import { Button } from '../../components/Button.jsx';
import { CheckInPostCard } from './BoardCards.jsx';
import { formatClock, isCrisisPost, seoulParts } from './boardFeed.js';

const EMPTY_LINE = '그냥 수다는 별채에서 해. 여긴 같이 고민할 것만 적어 둬.';

function nameOf(memberKey) {
    return getMember(memberKey)?.name ?? '';
}

/** 오늘이면 시각, 아니면 「10월 12일」 */
function whenOf(value, today) {
    const parts = seoulParts(value);
    const date = `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
    return date === today ? formatClock(value) : `${parts.month}월 ${parts.day}일`;
}

function StatusTag({ post }) {
    if (post.status === 'answered') {
        return (
            <span className="inline-flex h-6 items-center rounded-[12px] bg-surface-sunken px-2.5 text-[12px] font-semibold text-text-secondary">
                답을 받았어요
            </span>
        );
    }
    return (
        <span className="inline-flex h-6 items-center rounded-[12px] bg-[#F7E3D2] px-2.5 text-[12px] font-semibold text-[#8F4A14]">
            답하는 중
        </span>
    );
}

export function WorryCard({ post, today, onOpen, onRetry, retrying }) {
    const answered = post.answeredMemberKeys ?? [];
    return (
        <article className="flex flex-col gap-2.5 rounded-card border border-hairline bg-surface px-4 py-3.5">
            <button
                type="button"
                onClick={onOpen}
                className="flex flex-col gap-2.5 text-left"
                aria-label="고민 글 열기"
            >
                <span className="flex items-center gap-1.5">
                    <StatusTag post={post} />
                    {post.kind === 'day' ? (
                        <span className="inline-flex h-6 items-center rounded-[12px] border border-hairline px-2.5 text-[12px] font-semibold text-text-secondary">
                            내가 쓴 글
                        </span>
                    ) : null}
                    <span className="ml-auto text-[12px] text-text-secondary">{whenOf(post.createdAt, today)}</span>
                </span>
                <span className="line-clamp-3 text-[16px] leading-[26px] break-words whitespace-pre-wrap">
                    {post.body}
                </span>
                {answered.length ? (
                    <span className="flex items-center gap-2">
                        <span className="flex">
                            {answered.map((memberKey, index) => (
                                <span
                                    key={memberKey}
                                    className={`flex rounded-full border-[1.5px] border-surface ${index ? '-ml-1.5' : ''}`}
                                >
                                    <Avatar memberKey={memberKey} size={22} />
                                </span>
                            ))}
                        </span>
                        <span className="text-[12px] text-text-secondary">{answered.map(nameOf).join(', ')}</span>
                    </span>
                ) : null}
            </button>
            {post.status === 'failed' && onRetry ? (
                <Button variant="text" size="sm" className="self-start px-1" busy={retrying} onClick={onRetry}>
                    답 다시 받기
                </Button>
            ) : null}
            {post.status === 'stalled' ? (
                <p className="m-0 text-caption text-text-secondary">
                    답이 늦어지고 있어요. 조금 뒤에 다시 열어 보세요.
                </p>
            ) : null}
        </article>
    );
}

/**
 * @param {{ posts: object[]|null, today: string|null, error: string|null, displayName: string, checkInPostId: number|null,
 *   retryingId: number|null, collapsedSafety: Set<number>, onWrite: () => void, onOpen: (post) => void,
 *   onRetry: (postsId) => void, onCheckIn: (postsId) => void, onToggleSafety: (postsId, collapsed) => void,
 *   onReload: () => void }} props
 */
export function WorryTab({
    posts,
    today,
    error,
    displayName,
    checkInPostId,
    retryingId,
    collapsedSafety,
    onWrite,
    onOpen,
    onRetry,
    onCheckIn,
    onToggleSafety,
    onReload,
}) {
    return (
        <div className="flex flex-col gap-3 pt-3">
            <button
                type="button"
                onClick={onWrite}
                className="flex h-[52px] items-center justify-center gap-1.5 rounded-card bg-brand text-[15px] font-semibold text-on-brand"
            >
                <Plus size={20} strokeWidth={1.8} aria-hidden="true" />
                고민 올리기
            </button>
            {error ? (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <p role="alert" className="m-0 text-caption text-error">
                        {error}
                    </p>
                    <Button variant="secondary" size="md" onClick={onReload}>
                        다시 불러오기
                    </Button>
                </div>
            ) : null}
            {posts === null && !error ? (
                <p className="m-0 py-8 text-center text-caption text-text-secondary">고민 칸을 펼치는 중이에요.</p>
            ) : null}
            {posts?.length === 0 ? (
                <div className="flex items-start gap-2.5 py-4">
                    <Avatar memberKey="imo" size={40} />
                    <p className="m-0 rounded-bubble border border-hairline bg-surface px-3.5 py-2.5 font-serif text-body-member">
                        {EMPTY_LINE}
                    </p>
                </div>
            ) : null}
            {(posts ?? []).map((post) => (
                <div key={post.postsId} className="flex flex-col gap-3">
                    {post.checkIn ? (
                        <CheckInPostCard
                            post={post}
                            displayName={displayName}
                            busy={checkInPostId === post.postsId}
                            onStart={() => onCheckIn(post.postsId)}
                        />
                    ) : (
                        <WorryCard
                            post={post}
                            today={today}
                            onOpen={() => onOpen(post)}
                            onRetry={() => onRetry(post.postsId)}
                            retrying={retryingId === post.postsId}
                        />
                    )}
                    {isCrisisPost(post) ? (
                        <SafetyCard
                            level={post.safetyLevel}
                            collapsed={collapsedSafety.has(post.postsId)}
                            onCollapse={() => onToggleSafety(post.postsId, true)}
                            onExpand={() => onToggleSafety(post.postsId, false)}
                        />
                    ) : null}
                </div>
            ))}
        </div>
    );
}
